import { definitions } from "./KBSDefinitions";
import {requestChatStream} from "../API/chatAPI";
import {parseJSON} from "../App/utils";

const activateMonacoJSXHighlighter = async (monacoEditor, monacoInstance) => {
    const { default: traverse } = await import('@babel/traverse');
    const { parse } = await import('@babel/parser');
    const { default: MonacoJSXHighlighter } = await import('monaco-jsx-highlighter');

    const monacoJSXHighlighter = new MonacoJSXHighlighter(
        monacoInstance,
        parse,
        traverse,
        monacoEditor
    );

    monacoJSXHighlighter.highlightOnDidChangeModelContent();
    monacoJSXHighlighter.addJSXCommentCommand();

    return {
        monacoJSXHighlighter,
    };
}

function countCodeLines(code) {
    // Split the code string by newline characters and filter out empty lines
    const lines = code.split('\n').filter(line => line.trim() !== '');
    return lines.length;
}

const requestAIRefactorSelection = async ({selectedCode, editor, selection, KBData, codeAbortController, onResponse, Alert, showPrompt}) => {
    const codingModel = "gpt-4o";
    const prompt = await showPrompt({
        title: 'AI Refactor Selected Code',
        label: 'Type your prompt here',
        defaultValue: '',
    });

    let dynamicInstructions = ''

    if (prompt?.toLowerCase()?.includes('explain')) {
        dynamicInstructions += 'if user asks you to explain a code block, output the explanations in comments\n\n'
    }

    if (dynamicInstructions) {
        dynamicInstructions = 'You are AI Code Editor, read the USER_PROMPT and process the selected code:\n\n'
            + dynamicInstructions + 'USER_PROMPT:\n\n'
    }

    if (!prompt) return;

    const selectionLines = countCodeLines(selectedCode);
    Alert.setSystemAlert({ msg: `✨ Analyzing ${selectionLines} code line${selectionLines > 1 ? 's':''} ...`, type: 'info' });
    
    const params = {
        "chatPayload": {
            "model": codingModel,
            "messages": [
                {
                    "role": "user",
                    "content":
                        ''
                        // + "You are AI Code Editor:\n\n" +
                        + dynamicInstructions
                        + prompt
                        + '\n\n' + selectedCode
                        + '\n\n' + 'OUTPUT_FORMAT:\n\n' + '```javascript{outputCode}``` (one function only)'
                }
            ],
            "temperature": 0,
            "stream": true
        },
        "AESKey": KBData?.key
    };

    // Create a new AbortController for the new request
    if (codeAbortController.current) codeAbortController.current.abort();
    codeAbortController.current = new AbortController();

    const response = await requestChatStream(params, codeAbortController.current?.signal, KBData, true);

    // stopped
    if (!response?.body) return null;

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let incompleteMessage = '';
    let buffer = '';

    // Calculate the indentation level of the selected code
    const selectedCodeLines = selectedCode.split('\n');
    const firstLine = selectedCodeLines.find(line => line.trim() !== '');
    const indentationMatch = firstLine ? firstLine.match(/^\s*/) : null;
    const indentation = indentationMatch ? indentationMatch[0] : '';

    reader.read().then(async function processText({ done, value }) {
        if (done) {
            // Extract the last JavaScript code block from the buffer
            const codeBlockMatches = buffer.match(/```javascript([\s\S]*?)```/g);
            let generatedCode = '';

            if (codeBlockMatches && codeBlockMatches.length > 0) {
                const lastCodeBlock = codeBlockMatches[codeBlockMatches.length - 1];
                const lastCodeBlockMatch = lastCodeBlock.match(/```javascript([\s\S]*?)```/);
                generatedCode = lastCodeBlockMatch ? lastCodeBlockMatch[1].trim() : '';
            }

            // Adjust the indentation of the generated code
            if (generatedCode) {
                const generatedCodeLines = generatedCode.split('\n');
                const linesBefore = selectionLines
                const linesAfter = countCodeLines(generatedCode)

                let message;

                if (linesAfter < linesBefore) {
                    message = `✨ Code refactoring complete! Lines of code reduced from ${linesBefore} to ${linesAfter}.`;
                } else if (linesAfter > linesBefore) {
                    message = `✨ Code refactoring complete! Lines of code increased from ${linesBefore} to ${linesAfter}.`;
                } else {
                    message = `✨ Code refactoring complete! Lines of code remain the same: ${linesBefore}.`;
                }

                Alert.setSystemAlert({
                    msg: message,
                    type: 'success',
                    duration: 10000
                });
                const indentedGeneratedCode = generatedCodeLines.map(line => indentation + line).join('\n');

                // Replace the selected code in the editor with the indented generated code
                editor.executeEdits("", [{ range: selection, text: indentedGeneratedCode }]);
                onResponse({ generatedCode, selectedCode, done: true })
            }
            return;
        }

        const jsonMessages = decoder.decode(value).split('\n');

        for (let msg of jsonMessages) {
            if (!msg) continue;
            let data = parseJSON(incompleteMessage + msg);
            if (data === undefined) {
                incompleteMessage += msg;
                continue;
            } else {
                incompleteMessage = '';
            }

            // Update buffer with the latest content
            buffer += data?.content || '';

            const codeBlockMatches = buffer.match(/```javascript([\s\S]*?)(?=```|$)/g);
            let generatedCode = '';
            if (codeBlockMatches && codeBlockMatches.length > 0) {
                const lastCodeBlock = codeBlockMatches[codeBlockMatches.length - 1];
                const lastCodeBlockMatch = lastCodeBlock.match(/```javascript([\s\S]*?)(?=```|$)/);
                generatedCode = lastCodeBlockMatch ? lastCodeBlockMatch[1].trim() : '';
            }

            if (generatedCode) {
                // console.log(generatedCode)
                const solution = codeBlockMatches.length > 1 ? ('solution ' + codeBlockMatches.length)  : '';
                const lines = countCodeLines(generatedCode);
                Alert.setSystemAlert({
                    msg: `✨ Refactoring ${solution}: ${lines} line${lines !== 1 ? 's' : ''} in progress...`,
                    type: 'info'
                });
                onResponse({ generatedCode, selectedCode })
            }
        }

        reader.read().then(processText).catch(() => {
            console.log('Stream aborted');
        });
    });
};

export const createHandleEditorDidMount = ({ setEditors, selectRight, selectLeft, onResponse, Alert, showPrompt }) => {
    return async (editor, monaco, Key, KBData, codeAbortController, setDiffs) => {
        setEditors(prev => ({ ...prev, [Key]: editor }));

        let originalContent = editor.getValue();
        let decorations = [];

        const updateDiffs = (resetOriginalContent) => {
            if (resetOriginalContent) originalContent = editor.getValue()
            const currentContent = editor.getValue();
            const originalLines = originalContent.split('\n');
            const currentLines = currentContent.split('\n');
            const newDiffs = [];
            const newDecorations = [];

            for (let i = 0; i < Math.max(currentLines.length, originalLines.length); i++) {
                if (currentLines[i] !== originalLines[i]) {
                    newDiffs.push(i + 1);
                    newDecorations.push({
                        range: new monaco.Range(i + 1, 1, i + 1, 1),
                        options: {
                            isWholeLine: true,
                            linesDecorationsClassName: 'myLineDecoration'
                        }
                    });
                }
            }

            setDiffs(prev => ({ ...prev, [Key]: newDiffs }));
            decorations = editor.deltaDecorations(decorations, newDecorations);
        };

        editor.onDidChangeModelContent(() => {
            // avoid calculating diff before and after file loads (to avoid all the code to be colored green initially)
            if (!originalContent) {
                originalContent = editor.getValue();
            } else {
                updateDiffs();
            }
        });

        // Add custom CSS for the line decoration
        const style = document.createElement('style');
        style.textContent = `
        .myLineDecoration {
            background: green;
            width: 5px !important;
            margin-left: 3px;
        }
    `;
        document.head.appendChild(style);

        // Add JSX syntax highlighting
        await activateMonacoJSXHighlighter(editor, monaco);

        editor.addAction({
            id: "moveTabRight",
            label: "Move to Next Tab",
            keybindings: [monaco.KeyCode.RightArrow | monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift],
            run: () => {
                selectRight();
            }
        });

        editor.addAction({
            id: "moveTabLeft",
            label: "Move to Previous Tab",
            keybindings: [monaco.KeyCode.LeftArrow | monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift],
            run: () => {
                selectLeft();
            }
        });

        editor.addAction({
            id: "aiModifySelection",
            label: "✨ Modify Selected Code",
            keybindings: [monaco.KeyCode.KeyA | monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift],
            contextMenuGroupId: 'navigation',
            contextMenuOrder: 0.5, // Set to a lower value to appear first
            precondition: 'editorHasSelection',
            run: async () => {
                const selection = editor.getSelection();
                const selectedCode = editor.getModel().getValueInRange(selection);
                await requestAIRefactorSelection({selectedCode, editor, selection, KBData, codeAbortController, onResponse, Alert, showPrompt});
            }
        });

        // // Add "Select All" action
        editor.addAction({
            id: "selectAll",
            label: "Select All",
            precondition: '!editorHasSelection',
            keybindings: [monaco.KeyCode.KeyA | monaco.KeyMod.CtrlCmd],
            contextMenuGroupId: 'navigation',
            contextMenuOrder: 0.6, // Set to a lower value to appear first
            run: () => {
                const model = editor.getModel();
                const fullRange = model.getFullModelRange();
                editor.setSelection(fullRange);
            }
        });

        for (let definition of definitions) {
            monaco.languages.typescript.javascriptDefaults.addExtraLib(definition);
        }

        // Intercept the undo command to revert back from empty state
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyZ, () => {
            const model = editor.getModel();
            editor.trigger('keyboard', 'undo', null);
            const newValue = model.getValue();
            // If the new value is empty, redo the undo operation to revert back from empty state
            if (!newValue) editor.trigger('keyboard', 'redo', null)
        });

        return updateDiffs
    }
}